import type { HelpTopic } from './FAQContent';
import type { GuidedFlow, GuidedFlowNode } from './FAQFlow';

export type HelpSearchResultKind = 'topic' | 'article' | 'guide';

export type HelpSearchResult = {
  id: string;
  kind: HelpSearchResultKind;
  topicKey: string;
  topicTitle: string;
  title: string;
  summary: string;
  score: number;
  articleKey?: string;
  flowKey?: string;
  flowTitle?: string;
  flowNodeId?: string;
};

type HelpSearchEntry = Omit<HelpSearchResult, 'score'> & {
  titleSearchText: string;
  topicSearchText: string;
  keywordSearchText: string;
  bodySearchText: string;
  order: number;
};

function normalizeHelpSearchText(value: string) {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenizeHelpSearchText(value: string) {
  return normalizeHelpSearchText(value)
    .split(' ')
    .filter((term) => term.length >= 2);
}

function uniqueTerms(terms: string[]) {
  return Array.from(new Set(terms));
}

function getNodeSearchText(node: GuidedFlowNode) {
  if (node.type === 'question') {
    const answerText = node.answers.reduce<string[]>(
      (result, answer) =>
        result.concat([answer.label, answer.description || '', ...(answer.searchKeywords || [])]),
      []
    );

    return [
      node.title,
      node.question,
      node.helperText || '',
      ...(node.searchKeywords || []),
      ...answerText,
    ].join(' ');
  }

  return [
    node.title,
    node.summary,
    ...node.nextSteps,
    node.supportText || '',
    ...(node.searchKeywords || []),
  ].join(' ');
}

function getFlowSearchText(flow: GuidedFlow) {
  return [
    flow.title,
    flow.description,
    ...(flow.searchKeywords || []),
    ...Object.keys(flow.nodes).map((nodeId) => getNodeSearchText(flow.nodes[nodeId])),
  ].join(' ');
}

function getNodeSummary(node: GuidedFlowNode) {
  return node.type === 'question' ? node.question : node.summary;
}

function getNodeKeywordText(node: GuidedFlowNode) {
  if (node.type === 'question') {
    const answerKeywords = node.answers.reduce<string[]>(
      (result, answer) => result.concat(answer.searchKeywords || []),
      []
    );

    return [...(node.searchKeywords || []), ...answerKeywords].join(' ');
  }

  return (node.searchKeywords || []).join(' ');
}

function createHelpSearchEntries(topics: HelpTopic[]) {
  let order = 0;

  return topics.reduce<HelpSearchEntry[]>((entries, topic) => {
    const topicEntry: HelpSearchEntry = {
      id: `topic:${topic.key}`,
      kind: 'topic',
      topicKey: topic.key,
      topicTitle: topic.title,
      title: topic.title,
      summary: topic.summary,
      titleSearchText: normalizeHelpSearchText(topic.title),
      topicSearchText: normalizeHelpSearchText(topic.title),
      keywordSearchText: normalizeHelpSearchText((topic.searchKeywords || []).join(' ')),
      bodySearchText: normalizeHelpSearchText(topic.summary),
      order,
    };
    order += 1;

    const articleEntries = (topic.articles || []).map<HelpSearchEntry>((article) => {
      const entry = {
        id: `article:${topic.key}:${article.key}`,
        kind: 'article' as const,
        topicKey: topic.key,
        topicTitle: topic.title,
        title: article.title,
        summary: article.description[0],
        articleKey: article.key,
        titleSearchText: normalizeHelpSearchText(article.title),
        topicSearchText: normalizeHelpSearchText(topic.title),
        keywordSearchText: normalizeHelpSearchText((article.searchKeywords || []).join(' ')),
        bodySearchText: normalizeHelpSearchText(article.description.join(' ')),
        order,
      };
      order += 1;

      return entry;
    });

    const flowEntries = (topic.guidedFlows || []).reduce<HelpSearchEntry[]>((result, flow) => {
      const flowEntry: HelpSearchEntry = {
        id: `flow:${topic.key}:${flow.key}`,
        kind: 'guide',
        topicKey: topic.key,
        topicTitle: topic.title,
        title: flow.title,
        summary: flow.description,
        flowKey: flow.key,
        flowTitle: flow.title,
        titleSearchText: normalizeHelpSearchText(flow.title),
        topicSearchText: normalizeHelpSearchText(topic.title),
        keywordSearchText: normalizeHelpSearchText((flow.searchKeywords || []).join(' ')),
        bodySearchText: normalizeHelpSearchText(getFlowSearchText(flow)),
        order,
      };
      order += 1;

      const nodeEntries = Object.keys(flow.nodes).map<HelpSearchEntry>((nodeId) => {
        const node = flow.nodes[nodeId];
        const entry = {
          id: `flow-node:${topic.key}:${flow.key}:${node.id}`,
          kind: 'guide' as const,
          topicKey: topic.key,
          topicTitle: topic.title,
          title: node.title,
          summary: getNodeSummary(node),
          flowKey: flow.key,
          flowTitle: flow.title,
          flowNodeId: node.id,
          titleSearchText: normalizeHelpSearchText(node.title),
          topicSearchText: normalizeHelpSearchText(topic.title),
          keywordSearchText: normalizeHelpSearchText(getNodeKeywordText(node)),
          bodySearchText: normalizeHelpSearchText(getNodeSearchText(node)),
          order,
        };
        order += 1;

        return entry;
      });

      return result.concat([flowEntry, ...nodeEntries]);
    }, []);

    return entries.concat([topicEntry, ...articleEntries, ...flowEntries]);
  }, []);
}

function scoreTermInField(field: string, term: string, exactWeight: number, partialWeight: number) {
  if (!field) {
    return 0;
  }

  if (field.split(' ').includes(term)) {
    return exactWeight;
  }

  return field.includes(term) ? partialWeight : 0;
}

function scoreEntry(entry: HelpSearchEntry, directTerms: string[], phrase: string) {
  const directScore = directTerms.reduce(
    (score, term) =>
      score +
      scoreTermInField(entry.titleSearchText, term, 28, 16) +
      scoreTermInField(entry.keywordSearchText, term, 32, 18) +
      scoreTermInField(entry.topicSearchText, term, 14, 8) +
      scoreTermInField(entry.bodySearchText, term, 8, 4),
    0
  );

  const phraseScore =
    phrase.length > 0 && entry.titleSearchText.includes(phrase)
      ? 44
      : phrase.length > 0 && entry.bodySearchText.includes(phrase)
      ? 16
      : 0;

  return directScore + phraseScore;
}

function getKindPriority(kind: HelpSearchResultKind) {
  if (kind === 'article') {
    return 0;
  }

  if (kind === 'guide') {
    return 1;
  }

  return 2;
}

export function searchHelpTopics(
  topics: HelpTopic[],
  query: string,
  limit = 8
): HelpSearchResult[] {
  const directTerms = uniqueTerms(tokenizeHelpSearchText(query));

  if (directTerms.length === 0) {
    return [];
  }

  const phrase = normalizeHelpSearchText(query);

  return createHelpSearchEntries(topics)
    .map((entry) => ({
      ...entry,
      score: scoreEntry(entry, directTerms, phrase),
    }))
    .filter((entry) => entry.score > 0)
    .sort((first, second) => {
      if (second.score !== first.score) {
        return second.score - first.score;
      }

      const kindDifference = getKindPriority(first.kind) - getKindPriority(second.kind);

      return kindDifference !== 0 ? kindDifference : first.order - second.order;
    })
    .slice(0, limit)
    .map(
      ({ titleSearchText, topicSearchText, keywordSearchText, bodySearchText, order, ...result }) =>
        result
    );
}
